import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Divider,
  Flex,
  Grid,
  GridItem,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Spinner,
  Stack,
  Text,
  useToast,
} from "@chakra-ui/react";
import React, { useMemo, useState } from "react";
import { showErrorToast, showSuccessToast } from "../../../utils";
import { filterActiveDimensionItems } from "../../../utils/dimensions";
import {
  useMigratePreview,
  useMigrateDimensionData,
} from "../../../hooks/query/dimensions/dimensionAdmin";
import { useGetDimensions } from "../../../hooks/query/dimensions/dimensions";

// Response key (dimensionOne..) -> backend DimensionLevel enum.
const LEVELS = [
  { key: "dimensionOne", level: "LEVEL_ONE" },
  { key: "dimensionTwo", level: "LEVEL_TWO" },
  { key: "dimensionThree", level: "LEVEL_THREE" },
  { key: "dimensionFour", level: "LEVEL_FOUR" },
  { key: "dimensionFive", level: "LEVEL_FIVE" },
];

// Sentinel values for the destination select (distinct from any item id).
const LEAVE_UNCHANGED = "";
const SET_EMPTY = "__CLEAR__";

// Lime focus per theme convention (no default blue ring).
const focusSx = {
  _hover: { borderColor: "gray.400" },
  _focus: { borderColor: "lime", boxShadow: "0 0 0 1px var(--chakra-colors-lime)" },
  _focusVisible: { borderColor: "lime", boxShadow: "0 0 0 1px var(--chakra-colors-lime)" },
};

// Table label -> display, mirroring the backend DimensionedTable.label() keys.
const TABLE_LABELS = {
  questionnaires: "Questionnaire-level tags",
  scope1DirectEmissions: "Scope 1 direct emissions",
  scope2Electricity: "Scope 2 electricity",
  scope2PurchasedElectricity: "Scope 2 purchased electricity (v2)",
  scope2OnsiteGeneration: "Scope 2 onsite generation (v2)",
  manualEmissions: "Manual lump-sum emissions",
  businessTravel: "Business travel",
  capitalGoods: "Capital goods",
  employeeCommuting: "Employee commuting",
  investments: "Investments",
  purchasedGoodsAndServices: "Purchased goods and services",
  transportationDistribution: "Transportation and distribution",
  treatmentOfSoldProducts: "Treatment of sold products",
  useOfSoldProducts: "Use of sold products",
  wasteGeneratedInOperations: "Waste generated in operations",
  wastewater: "Wastewater",
  waterUsage: "Water usage",
  taskAssigneeTemplates: "Task assignee templates",
};

function MigrateDimensionDataModal({ isOpen, onClose, onSuccess }) {
  const toast = useToast();
  const [stage, setStage] = useState("build"); // "build" | "preview"
  const [sourceByLevel, setSourceByLevel] = useState({}); // { LEVEL_ONE: itemId }
  const [destByLevel, setDestByLevel] = useState({}); // { LEVEL_ONE: itemId | SET_EMPTY }
  const [previewData, setPreviewData] = useState(null);

  const { data: dimensionsData } = useGetDimensions({}, { enabled: isOpen });

  // Only levels the org has actually configured (present in the response with items).
  const configuredLevels = useMemo(() => {
    if (!dimensionsData) return [];
    return LEVELS.filter((l) => dimensionsData[l.key]).map((l) => ({
      ...l,
      name: dimensionsData[l.key].name || l.key,
      items: dimensionsData[l.key].items || [],
    }));
  }, [dimensionsData]);

  const buildRequest = () => {
    const source = Object.entries(sourceByLevel)
      .filter(([, itemId]) => itemId)
      .map(([level, itemId]) => ({ level, itemId: Number(itemId) }));
    const destination = Object.entries(destByLevel)
      .filter(([, value]) => value !== LEAVE_UNCHANGED && value !== undefined)
      .map(([level, value]) =>
        value === SET_EMPTY
          ? { level, action: "CLEAR", itemId: null }
          : { level, action: "SET", itemId: Number(value) },
      );
    return { source, destination };
  };

  const request = buildRequest();
  const canPreview = request.source.length > 0 && request.destination.length > 0;

  const { mutate: runPreview, isLoading: isPreviewLoading } = useMigratePreview({
    onSuccess: (result) => {
      setPreviewData(result);
      setStage("preview");
    },
    onError: (err) => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
  });

  const { mutate: runMigrate, isLoading: isMigrating } = useMigrateDimensionData({
    onSuccess: (result) => {
      showSuccessToast(
        toast,
        `Migration applied. ${result.totalRows} record(s) re-tagged, ` +
          `${result.reportsReflagged} report(s) queued for recalculation.`,
      );
      onSuccess();
      handleClose();
    },
    onError: (err) => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
  });

  const handleClose = () => {
    setStage("build");
    setSourceByLevel({});
    setDestByLevel({});
    setPreviewData(null);
    onClose();
  };

  const levelName = (level) => configuredLevels.find((l) => l.level === level)?.name ?? level;
  const itemName = (level, itemId) => {
    const found = configuredLevels
      .find((l) => l.level === level)
      ?.items.find((i) => String(i.id) === String(itemId));
    return found?.name ?? itemId;
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Migrate dimension data</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {stage === "build" ? (
            <Stack spacing={4}>
              <Text fontSize="sm" color="gray.600">
                Re-tag all data matching the source filter with the destination values. The source
                filter matches rows where every chosen level equals the selected item (AND across
                levels). Destination levels left as "Leave unchanged" are not modified; choose "Set
                empty" to clear a level.
              </Text>

              <Grid templateColumns="1fr 1fr 1fr" gap={2} alignItems="center">
                <GridItem />
                <GridItem>
                  <Text fontSize="xs" fontWeight="semibold" color="gray.500">
                    SOURCE (match)
                  </Text>
                </GridItem>
                <GridItem>
                  <Text fontSize="xs" fontWeight="semibold" color="gray.500">
                    DESTINATION (set)
                  </Text>
                </GridItem>

                {configuredLevels.map((lvl) => {
                  const sourceItems = filterActiveDimensionItems(lvl.items, sourceByLevel[lvl.level]);
                  const destItems = filterActiveDimensionItems(lvl.items, destByLevel[lvl.level]);
                  return (
                    <React.Fragment key={lvl.level}>
                      <GridItem>
                        <Text fontSize="sm" fontWeight="medium">
                          {lvl.name}
                        </Text>
                      </GridItem>
                      <GridItem>
                        <Select
                          size="sm"
                          placeholder="Not filtered"
                          value={sourceByLevel[lvl.level] ?? ""}
                          onChange={(e) =>
                            setSourceByLevel((prev) => ({ ...prev, [lvl.level]: e.target.value }))
                          }
                          sx={focusSx}
                        >
                          {sourceItems.map((i) => (
                            <option key={i.id} value={i.id}>
                              {i.name}
                            </option>
                          ))}
                        </Select>
                      </GridItem>
                      <GridItem>
                        <Select
                          size="sm"
                          value={destByLevel[lvl.level] ?? LEAVE_UNCHANGED}
                          onChange={(e) =>
                            setDestByLevel((prev) => ({ ...prev, [lvl.level]: e.target.value }))
                          }
                          sx={focusSx}
                        >
                          <option value={LEAVE_UNCHANGED}>Leave unchanged</option>
                          <option value={SET_EMPTY}>— Set empty —</option>
                          {destItems.map((i) => (
                            <option key={i.id} value={i.id}>
                              {i.name}
                            </option>
                          ))}
                        </Select>
                      </GridItem>
                    </React.Fragment>
                  );
                })}
              </Grid>

              {!canPreview && (
                <Text fontSize="xs" color="gray.400">
                  Choose at least one source level and one destination assignment to continue.
                </Text>
              )}
            </Stack>
          ) : (
            <Stack spacing={4}>
              <Box>
                <Text fontSize="sm" fontWeight="semibold" mb={1}>
                  Source filter
                </Text>
                <Box p={2} bg="orange.50" borderRadius="md" border="1px solid" borderColor="orange.200">
                  {request.source.map((s) => (
                    <Text key={s.level} fontSize="sm">
                      {levelName(s.level)} = {itemName(s.level, s.itemId)}
                    </Text>
                  ))}
                </Box>
              </Box>

              <Box>
                <Text fontSize="sm" fontWeight="semibold" mb={1}>
                  Destination
                </Text>
                <Box p={2} bg="green.50" borderRadius="md" border="1px solid" borderColor="green.200">
                  {request.destination.map((d) => (
                    <Text key={d.level} fontSize="sm">
                      {levelName(d.level)} ={" "}
                      {d.action === "CLEAR" ? <em>(empty)</em> : itemName(d.level, d.itemId)}
                    </Text>
                  ))}
                </Box>
              </Box>

              <Divider />

              {isPreviewLoading ? (
                <Flex justify="center" py={4}>
                  <Spinner size="sm" />
                  <Text ml={2} fontSize="sm" color="gray.500">
                    Loading impact preview...
                  </Text>
                </Flex>
              ) : previewData ? (
                <Box>
                  <Text fontSize="sm" fontWeight="semibold" mb={2}>
                    Impact — {previewData.totalRows} record(s) will be re-tagged
                  </Text>
                  <Stack spacing={1}>
                    {Object.entries(previewData.rowsByTable ?? {})
                      .filter(([, count]) => count > 0)
                      .map(([key, count]) => (
                        <Flex key={key} justify="space-between" fontSize="sm" py={0.5}>
                          <Text color="gray.600">{TABLE_LABELS[key] ?? key}</Text>
                          <Text fontWeight="semibold">{count}</Text>
                        </Flex>
                      ))}
                    {previewData.totalRows === 0 && (
                      <Text fontSize="sm" color="gray.400">
                        No records match this source filter.
                      </Text>
                    )}
                  </Stack>
                </Box>
              ) : null}

              <Alert status="warning" borderRadius="md" fontSize="sm">
                <AlertIcon />
                This action cannot be undone. Matching data will be re-tagged to the destination
                values and affected By-Dimension reports will be recalculated automatically within
                10 minutes.
              </Alert>
            </Stack>
          )}
        </ModalBody>
        <ModalFooter gap={2}>
          {stage === "build" ? (
            <>
              <Button variant="ghost" onClick={handleClose}>
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={() => runPreview(request)}
                isDisabled={!canPreview}
                isLoading={isPreviewLoading}
              >
                Preview
              </Button>
            </>
          ) : (
            <>
              <Button variant="ghost" onClick={() => setStage("build")} isDisabled={isMigrating}>
                Back
              </Button>
              <Button
                colorScheme="red"
                onClick={() => runMigrate(request)}
                isLoading={isMigrating}
                isDisabled={isPreviewLoading || (previewData?.totalRows ?? 0) === 0}
              >
                Apply migration
              </Button>
            </>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export default MigrateDimensionDataModal;
